/*
    Copyright (C) 2017 Red Hat, Inc.

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

            http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/
import './index.css';
import * as serviceWorker from './serviceWorker';
import App from './App';
import { 
  AtlasmapProvider,
  AuthProvider,
  useAuth,
  TokenData,
  decodeJWTExpiration,
  decodeJWTUserInfo
} from '@atlasmap/atlasmap';
import React from 'react';
import ReactDOM from 'react-dom';

//fetch token from url query parameter;
let search = window.location.search.replace('?data=', ''); 
let convertedData = search ? atob(search) : ''
let query: { [key: string]: string } = {};  
if (convertedData) {  
  let params = new URLSearchParams(decodeURI(convertedData));  
  let entries = Array.from(params); // Convert to an array  
  entries.forEach(pair => {  
    query[pair[0]] = pair[1].replace(/"/g, '\\"');  
  });  
}  
 
//fetch token from query parameter
let token = query.token;
let refresh_token = query.refresh_token;

if (!refresh_token && !token) {
  alert('Unauthorized: Token not found');
  throw new Error('Unauthorized');
}

// Determine which token to use and which endpoint to call
const tokenToUse = refresh_token || token;

// Fetch portalEnv and iamEnv from query parameters
const portalEnv = query.portalEnv || '';
const iamEnv = query.iamEnv || '';

const endpointUrl = refresh_token 
  ? `v2/atlas/exchange-token?portalEnv=${encodeURIComponent(portalEnv)}&iamEnv=${encodeURIComponent(iamEnv)}`
  : 'v2/atlas/check-token';

fetch(endpointUrl, {    
  headers: {    
    'Authorization': `Bearer ${tokenToUse}`    
  }    
})    
.then(response => {    
  if (response.status === 401) {    
    alert('Unauthorized: Invalid token');    
    throw new Error('Unauthorized');    
  }    
  if (!response.ok) {    
    alert('Token verification failed');    
    throw new Error('Token check failed');    
  }

  if (refresh_token) {
    return response.json();
  }
  return null;
})
.then((tokenData: TokenData | null) => {
  ReactDOM.render(    
    <AuthProvider>
      <AppBootstrap 
        tokenData={tokenData} 
        originalToken={token} 
        usedRefreshToken={!!refresh_token}
      />
    </AuthProvider>,
    document.getElementById('root'),    
  );    
})    
.catch(error => {    
  console.error('Error:', error); 
  throw new Error('Unauthorized');   
}); 

// Bootstrap component to initialize auth context
function AppBootstrap({ 
  tokenData, 
  originalToken,
  usedRefreshToken
}: { 
  tokenData: TokenData | null;
  originalToken: string;
  usedRefreshToken: boolean;
}) {
  const { setTokenData } = useAuth();
  
  React.useEffect(() => {
    if (usedRefreshToken && tokenData) {
      // Scenario 1: Refresh token flow - use returned IAM access token
      // Has refresh capability, tokens will auto-refresh
      setTokenData(tokenData);
    } else {
      // Scenario 2: Direct token flow - use original token for all API calls - for UIBakery
      // No refresh capability, extract expiry from JWT token itself
      const iamTokenExpiresAt = decodeJWTExpiration(originalToken);
      const userInfo = decodeJWTUserInfo(originalToken);
      
      setTokenData({
        iamAccessToken: originalToken, // Use original token for all API calls - UIBakery
        iamTokenExpiresAt: iamTokenExpiresAt!,
        azureAccessToken: '',
        azureRefreshToken: '', // No refresh token in direct token flow
        azureTokenExpiresAt: '', // No Azure token in check-token flow
        userInfo: userInfo || { email: '', name: '' },
      });
    }
  }, [tokenData, originalToken, usedRefreshToken, setTokenData]);

  return (
    <AtlasmapProvider    
      baseJavaInspectionServiceUrl={'/v2/atlas/java/'}    
      baseXMLInspectionServiceUrl={'/v2/atlas/xml/'}    
      baseJSONInspectionServiceUrl={'/v2/atlas/json/'}    
      baseCSVInspectionServiceUrl={'/v2/atlas/csv/'}    
      baseMappingServiceUrl={'/v2/atlas/'}    
      logLevel={'info'}    
    >    
      <App />    
    </AtlasmapProvider>
  );
}


// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
